import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Loader2, User, Mail, Phone } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useRateLimit } from '@/hooks/useRateLimit';
import OTPVerificationModal from './OTPVerificationModal';

interface CheckoutAuthStepProps {
  onNext: (
    businessDetails?: { taxId?: string; businessName?: string; businessAddress?: string },
    personalDetails?: { firstName: string; lastName: string; phone: string; email: string }
  ) => void;
  onBack: () => void;
  initialData?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    taxId?: string;
    businessName?: string;
    businessAddress?: string;
  } | null;
}

const CheckoutAuthStep = ({ onNext, onBack, initialData }: CheckoutAuthStepProps) => {
  const { user, profile, updateProfile } = useAuth();
  const [mode, setMode] = useState<'check-email' | 'login' | 'signup'>('check-email');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [isBusinessInvoice, setIsBusinessInvoice] = useState(!!initialData?.businessName || !!(profile as any)?.business_name);

  // Rate limiting for email checks to prevent enumeration attacks
  const { checkRateLimit, isBlocked, getRemainingAttempts } = useRateLimit('email-check', {
    maxAttempts: 5,
    timeWindow: 5, // 5 minutes
    warningThreshold: 3,
  });

  const [formData, setFormData] = useState({
    email: initialData?.email || '',
    password: '',
    confirmPassword: '',
    firstName: initialData?.firstName || profile?.first_name || '',
    lastName: initialData?.lastName || profile?.last_name || '',
    phone: initialData?.phone || profile?.phone || '',
    taxId: initialData?.taxId || (profile as any)?.tax_id || '',
    businessName: initialData?.businessName || (profile as any)?.business_name || '',
    businessAddress: initialData?.businessAddress || (profile as any)?.business_address || '',
  });

  // Effect to set mode if email is already present (e.g. from initialData)
  useEffect(() => {
    if (initialData?.email && !user) {
      if (initialData.firstName) {
        setMode('signup');
      }
    }
  }, [initialData, user]);

  // Effect to populate form data from profile when available
  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        firstName: prev.firstName || profile.first_name || '',
        lastName: prev.lastName || profile.last_name || '',
        phone: prev.phone || profile.phone || '',
        // Use type assertion for potential custom fields if they aren't in the base type definition yet
        taxId: prev.taxId || (profile as any)?.tax_id || '',
        businessName: prev.businessName || (profile as any)?.business_name || '',
        businessAddress: prev.businessAddress || (profile as any)?.business_address || '',
        email: profile.email || prev.email || '',
      }));

      // Auto-enable business invoice if business name exists in profile
      if ((profile as any)?.business_name && !isBusinessInvoice) {
        setIsBusinessInvoice(true);
      }
    }
  }, [profile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Normalize phone input: keep digits only to prevent email autofill or invalid characters
    let normalizedValue = value;

    if (name === 'phone') {
      // Remove all non-digit characters first
      normalizedValue = value.replace(/[^\d+]/g, '');

      // Handle Israeli IDD prefix cases
      if (normalizedValue.startsWith('+972')) {
        normalizedValue = '0' + normalizedValue.slice(4);
      } else if (normalizedValue.startsWith('972')) {
        normalizedValue = '0' + normalizedValue.slice(3);
      }

      // Strip anything that isn't a digit for the final value
      normalizedValue = normalizedValue.replace(/[^\d]/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: normalizedValue,
    }));
  };

  const checkEmailExists = async () => {
    // Check rate limit first
    if (!checkRateLimit()) {
      return; // Toast is already shown by the rate limit hook
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-email-exists', {
        body: { email: formData.email }
      });

      if (error) {
        toast.error("שגיאה", {
          description: "לא ניתן לבדוק את כתובת האימייל",
        });
        return;
      }

      if (data.exists) {
        setMode('login');
        toast.success("זהינו שהמשתמש קיים במערכת", {
          description: "יש להתחבר",
        });
      } else {
        setMode('signup');
        // Removed toast to not confuse guest users
      }
    } catch (error) {
      console.error('Error checking email:', error);
      // Removed generic error toast on check failure to avoid blocking user flow seamlessly
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) {
        toast.error("שגיאה בהתחברות", {
          description: "שם משתמש או סיסמה שגויים",
        });
        return;
      }

      toast.success("התחברת בהצלחה", {
        description: "אנא אשר את הפרטים שלך",
      });
    } catch (error) {
      console.error('Login error:', error);
      toast.error("שגיאה בהתחברות", {
        description: "אירעה שגיאה בהתחברות",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = () => {
    // Validate required personal fields
    if (!formData.firstName || !formData.lastName || !formData.phone) {
      toast.error("שדות חסרים", { description: "אנא מלא את כל השדות הנדרשים" });
      return;
    }

    const personalDetails = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email
    };

    const businessDetails = {
      taxId: formData.taxId || undefined,
      businessName: formData.businessName || undefined,
      businessAddress: formData.businessAddress || undefined,
    };

    // GUEST FLOW
    if (!createAccount) {
      onNext(businessDetails, personalDetails);
      return;
    }

    // ACCOUNT CREATION FLOW
    // Validate password fields
    if (!formData.password || !formData.confirmPassword) {

      // Check if passwords match
      if (formData.password !== formData.confirmPassword) {
        toast.error("סיסמאות לא תואמות", {
          description: "הסיסמה ואימות הסיסמה חייבים להיות זהים",
        });
        return;
      }

      // Check password length
      if (formData.password.length < 6) {
        toast.error("סיסמה חלשה", {
          description: "הסיסמה חייבת להכיל לפחות 6 תווים",
        });
        return;
      }
    }

    // Open OTP modal - Moved outside the validation block so it runs when inputs are valid
    setShowOTPModal(true);
  };

  const handleOTPSuccess = () => {
    setShowOTPModal(false);
    toast.success("התחברת בהצלחה", {
      description: "מעבר לשלב הבא...",
    });
    const businessDetails = {
      taxId: formData.taxId || undefined,
      businessName: formData.businessName || undefined,
      businessAddress: formData.businessAddress || undefined,
    };

    // For signed up user, we can get details from formData as well
    const personalDetails = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email
    };

    setTimeout(() => {
      onNext(businessDetails, personalDetails);
    }, 1000);
  };

  const handleUpdateProfile = async () => {
    // Check if anything actually changed
    const hasChanges =
      formData.firstName !== (profile?.first_name || '') ||
      formData.lastName !== (profile?.last_name || '') ||
      formData.phone !== (profile?.phone || '') ||
      formData.taxId !== ((profile as any)?.tax_id || '') ||
      formData.businessName !== ((profile as any)?.business_name || '') ||
      formData.businessAddress !== ((profile as any)?.business_address || '');

    const businessDetails = {
      taxId: formData.taxId || undefined,
      businessName: formData.businessName || undefined,
      businessAddress: formData.businessAddress || undefined,
    };

    // If no changes, just proceed to next step with business details
    if (!hasChanges) {
      onNext(businessDetails, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: profile?.email || formData.email
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await updateProfile({
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        tax_id: formData.taxId || null,
        business_name: formData.businessName || null,
        business_address: formData.businessAddress || null,
      });

      if (result.error) {
        toast.error("שגיאה", {
          description: "לא ניתן לעדכן את הפרטים",
        });
        return;
      }

      toast.success("הפרטים עודכנו", {
        description: "מעבר לשלב הבא...",
      });

      setTimeout(() => {
        onNext(businessDetails, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          email: profile?.email || formData.email
        });
      }, 1000);
    } catch (error) {
      console.error('Update profile error:', error);
      toast.error("שגיאה", {
        description: "אירעה שגיאה בעדכון הפרטים",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // If user is already logged in, show profile verification
  if (user && profile) {
    return (
      <div className="space-y-6" dir="rtl">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">כתובת אימייל</Label>
                <Input
                  id="email"
                  value={profile.email}
                  disabled
                  className="text-right bg-muted"
                  dir="rtl"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName"><span className="text-destructive">* </span>שם פרטי</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="שם פרטי"
                    required
                    className="text-right"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName"><span className="text-destructive">* </span>שם משפחה</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="שם משפחה"
                    required
                    className="text-right"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone"><span className="text-destructive">* </span>טלפון</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  pattern="[0-9]*"
                  autoCorrect="off"
                  autoCapitalize="off"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="05x-xxxxxxx"
                  required
                  className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs"
                  dir="rtl"
                />
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="flex items-center space-x-2 space-x-reverse mb-4">
                  <Checkbox
                    id="isBusinessInvoice"
                    checked={isBusinessInvoice}
                    onCheckedChange={(checked) => setIsBusinessInvoice(checked as boolean)}
                  />
                  <Label htmlFor="isBusinessInvoice" className="font-medium cursor-pointer">
                    חשבונית על שם עסק?
                  </Label>
                </div>

                {isBusinessInvoice && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="space-y-2">
                      <Label htmlFor="taxId">ח.פ. / עוסק מורשה</Label>
                      <Input
                        id="taxId"
                        name="taxId"
                        value={formData.taxId}
                        onChange={handleInputChange}
                        placeholder="מספר עוסק מורשה / ח.פ."
                        className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs"
                        dir="rtl"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="businessName">שם עסק</Label>
                      <Input
                        id="businessName"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleInputChange}
                        placeholder="שם העסק"
                        className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs"
                        dir="rtl"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="businessAddress">כתובת עסק</Label>
                      <Input
                        id="businessAddress"
                        name="businessAddress"
                        value={formData.businessAddress}
                        onChange={handleInputChange}
                        placeholder="כתובת העסק המלאה"
                        className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs"
                        dir="rtl"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4 items-center">
          <button
            onClick={onBack}
            className="text-foreground hover:text-foreground/80 transition-colors flex items-center gap-1"
          >
            <span className="text-sm">&lt;</span>
            <span>חזור</span>
          </button>
          <Button
            onClick={handleUpdateProfile}
            disabled={isLoading || !formData.firstName || !formData.lastName || !formData.phone}
            className="flex-1 bg-lime text-lime-foreground hover:bg-lime/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                שומר...
              </>
            ) : 'המשך'}
          </Button>
        </div>
      </div>
    );
  }

  // Email check mode
  if (mode === 'check-email') {
    return (
      <div className="space-y-6" dir="rtl">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">כתובת אימייל</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="הכנס את כתובת האימייל שלך"
                required
                className="text-right"
                dir="rtl"
              />
            </div>
          </CardContent>
        </Card>

        {isBlocked && (
          <Alert variant="destructive">
            <AlertDescription className="text-right" dir="rtl">
              בוצעו יותר מדי ניסיונות בדיקת אימייל. אנא נסה שוב מאוחר יותר.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-4 items-center">
          <button
            onClick={onBack}
            className="text-foreground hover:text-foreground/80 transition-colors flex items-center gap-1"
          >
            <span className="text-sm">&lt;</span>
            <span>חזור</span>
          </button>
          <Button
            onClick={checkEmailExists}
            disabled={isLoading || !formData.email || isBlocked}
            className="flex-1 bg-lime text-lime-foreground hover:bg-lime/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                בודק...
              </>
            ) : 'המשך'}
          </Button>
        </div>
      </div>
    );
  }

  // Login mode
  if (mode === 'login') {
    return (
      <div className="space-y-6" dir="rtl">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-foreground">
              זהינו שהמשתמש קיים במערכת, יש להתחבר
            </p>

            <div className="space-y-2">
              <Label>כתובת אימייל</Label>
              <Input
                value={formData.email}
                disabled
                className="text-right bg-muted"
                dir="rtl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">סיסמה</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="הכנס את הסיסמה שלך"
                  required
                  className="text-right pl-10"
                  dir="rtl"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4 items-center">
          <button
            onClick={() => setMode('check-email')}
            className="text-foreground hover:text-foreground/80 transition-colors flex items-center gap-1"
          >
            <span className="text-sm">&lt;</span>
            <span>חזור</span>
          </button>
          <Button
            onClick={handleLogin}
            disabled={isLoading || !formData.password}
            className="flex-1 bg-lime text-lime-foreground hover:bg-lime/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                מתחבר...
              </>
            ) : 'התחבר'}
          </Button>
        </div>
      </div>
    );
  }

  // Signup mode
  return (
    <>
      <div className="space-y-6" dir="rtl">
        <Card>
          <CardContent className="pt-6 space-y-4">
            <p className="text-muted-foreground text-sm">
              הזן את פרטיך להמשך הזמנה
            </p>

            <div className="space-y-2">
              <Label>כתובת אימייל</Label>
              <Input
                value={formData.email}
                disabled
                className="text-right bg-muted"
                dir="rtl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName"><span className="text-destructive">* </span>שם פרטי</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="שם פרטי"
                  required
                  className="text-right"
                  dir="rtl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName"><span className="text-destructive">* </span>שם משפחה</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="שם משפחה"
                  required
                  className="text-right"
                  dir="rtl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone"><span className="text-destructive">* </span>טלפון</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                pattern="[0-9]*"
                autoCorrect="off"
                autoCapitalize="off"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="05x-xxxxxxx"
                required
                className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs"
                dir="rtl"
              />
            </div>

            <div className="flex items-center space-x-2 space-x-reverse pt-2 border-t">
              <Checkbox
                id="createAccount"
                checked={createAccount}
                onCheckedChange={(checked) => setCreateAccount(checked as boolean)}
              />
              <Label htmlFor="createAccount" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                אני רוצה לפתוח חשבון משתמש
              </Label>
            </div>

            {createAccount && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="password"><span className="text-destructive">* </span>צור סיסמה</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="לפחות 6 תווים"
                      required
                      className="text-right pl-10"
                      dir="rtl"
                      minLength={6}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword"><span className="text-destructive">* </span>אימות סיסמה</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="הכנס את הסיסמה שוב"
                      required
                      className="text-right pl-10"
                      dir="rtl"
                      minLength={6}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-xs text-destructive">הסיסמאות אינן תואמות</p>
                  )}
                </div>
              </>
            )}

            <div className="border-t pt-4 mt-4">
              <div className="flex items-center space-x-2 space-x-reverse mb-4">
                <Checkbox
                  id="isBusinessInvoiceGuest"
                  checked={isBusinessInvoice}
                  onCheckedChange={(checked) => setIsBusinessInvoice(checked as boolean)}
                />
                <Label htmlFor="isBusinessInvoiceGuest" className="font-medium cursor-pointer">
                  חשבונית על שם עסק?
                </Label>
              </div>

              {isBusinessInvoice && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="space-y-2">
                    <Label htmlFor="taxId">ח.פ. / עוסק מורשה</Label>
                    <Input
                      id="taxId"
                      name="taxId"
                      value={formData.taxId}
                      onChange={handleInputChange}
                      placeholder="מספר עוסק מורשה / ח.פ."
                      className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs"
                      dir="rtl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessName">שם עסק</Label>
                    <Input
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      placeholder="שם העסק"
                      className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs"
                      dir="rtl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessAddress">כתובת עסק</Label>
                    <Input
                      id="businessAddress"
                      name="businessAddress"
                      value={formData.businessAddress}
                      onChange={handleInputChange}
                      placeholder="כתובת העסק המלאה"
                      className="text-right placeholder:text-muted-foreground/40 placeholder:text-xs"
                      dir="rtl"
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4 items-center">
          <button
            onClick={() => setMode('check-email')}
            className="text-foreground hover:text-foreground/80 transition-colors flex items-center gap-1"
          >
            <span className="text-sm">&lt;</span>
            <span>חזור</span>
          </button>
          <Button
            onClick={handleSignup}
            disabled={
              isLoading ||
              !formData.firstName ||
              !formData.lastName ||
              !formData.phone ||
              (createAccount && (!formData.password || !formData.confirmPassword || formData.password !== formData.confirmPassword))
            }
            className="flex-1 bg-lime text-lime-foreground hover:bg-lime/90"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {createAccount ? 'נרשם...' : 'מעבד...'}
              </>
            ) : (createAccount ? 'הירשם' : 'המשך כאורח')}
          </Button>
        </div>
      </div>

      <OTPVerificationModal
        isOpen={showOTPModal}
        onClose={() => setShowOTPModal(false)}
        email={formData.email}
        password={formData.password}
        firstName={formData.firstName}
        lastName={formData.lastName}
        phone={formData.phone}
        taxId={formData.taxId}
        businessName={formData.businessName}
        businessAddress={formData.businessAddress}
        onSuccess={handleOTPSuccess}
      />
    </>
  );
};

export default CheckoutAuthStep;
